export const GROK_VIDEO_EDITOR_PATH = '/ai-video-editor';
export const GROK_VIDEO_EDITOR_DATE_PUBLISHED = '2026-07-26';
export const GROK_VIDEO_EDITOR_LAST_UPDATED = '2026-07-27';
export const GROK_VIDEO_EDITOR_LAST_UPDATED_LABEL = 'July 27, 2026';
export const GROK_VIDEO_EDITOR_OG_IMAGE =
  '/video-examples/example-replace-character-poster.jpg';

export const GROK_VIDEO_EDITOR_SEO_TITLE =
  'Free AI Video Editor Online - Edit Videos with AI Prompts';
export const GROK_VIDEO_EDITOR_SEO_DESCRIPTION =
  'Free AI Video Editor Online transforms clips with text prompts. Change scenes, compare results, and download without a visible site watermark.';
export const GROK_VIDEO_EDITOR_KEYWORDS = [
  'free ai video editor',
  'free ai video editor online',
  'free ai video editor with prompt',
  'free ai video editor without watermark',
  'ai video editor',
  'online ai video editor',
  'ai video editor free',
  'edit video with ai',
  'ai video editing',
  'prompt based video editor',
  'prompt video editor',
  'edit video with text prompt',
  'ai video restyle',
  'video to video ai',
  'change video background with ai',
  'replace object in video with ai',
  'ai video editor with reference image',
  'free ai video editor for reels',
  'free ai video editor for tiktok',
  'free ai video editor for social media',
  'free ai video editor for youtube shorts',
].join(', ');

export const GROK_VIDEO_EDITOR_QUICK_ANSWER =
  'The Free AI Video Editor Online changes an existing MP4 or MOV through a text prompt. Start with the lowest-credit compatible model to test the direction, move to a balanced option when reference or audio control matters, and use premium resolution only after the edit works. Compare Original and Edited before downloading without an added visible site watermark.';

export const GROK_VIDEO_EDITOR_DIRECT_ANSWER =
  'Edit an existing 1–30 second MP4 or MOV with a text prompt. See the credit estimate before generation, compare Original and Edited, and download without a visible site watermark.';

export const GROK_VIDEO_EDITOR_RELATED_TOOLS = [
  {
    title: 'Grok Image to Video',
    description:
      'Animate a source frame, then refine the clip in the Free AI Video Editor.',
    href: '/image-to-video',
    image: '/image-to-video/robots-on-mars-preparation-poster.jpg',
    alt: 'Robots on Mars source frame for the Grok image-to-video workflow',
  },
  {
    title: 'Grok Video Generator',
    description:
      'Create a new short scene from text when you do not have a source clip.',
    href: '/text-to-video',
    image: '/text-to-video/text-to-video-perfume-product-scene-poster.jpg',
    alt: 'Perfume product scene from the Grok text-to-video workflow',
  },
  {
    title: 'AI Video Extender',
    description: 'Continue the ending when the clip needs more time.',
    href: '/ai-video-extender',
    image: '/ai-video-extender/examples/racing-motion-continuation.jpg',
    alt: 'Racing video motion continuation made for the AI Video Extender',
  },
  {
    title: 'AI Video Upscaler',
    description: 'Increase the size of an approved edit before delivery.',
    href: '/video-upscale',
    image: '/video-upscale/ai-generated-video-polish-after-poster.jpg',
    alt: 'Enhanced AI-generated landscape from the AI Video Upscaler',
  },
  {
    title: 'Grok Image Generator',
    description: 'Generate a new source image from a text prompt.',
    href: '/image-generator',
    image: '/grok-image-examples/1-1.webp',
    alt: 'AI image created with the Grok Image Generator',
  },
  {
    title: 'Grok AI Image Editor',
    description: 'Refine a source frame before using it in a video.',
    href: '/image-editor',
    image: '/grok-image-examples/1-2.webp',
    alt: 'AI image ready for editing in the Grok AI Image Editor',
  },
] as const;

export const GROK_VIDEO_EDITOR_PROOF_EXAMPLES = [
  {
    title: 'Keep motion while changing the visible character',
    description:
      'The character-replacement example demonstrates the review target for a reference-guided edit: body timing remains readable while the visible performer changes. Use the Free AI Video Editor reference image field when identity or object appearance matters, then inspect the face, hands, scale, lighting, and perspective across the complete clip.',
    prompt:
      'Replace the performer with the person in my reference image while preserving the original motion, timing, scale, camera angle, and scene lighting.',
    video: '/video-examples/example-replace-character.webm',
    poster: '/video-examples/example-replace-character-poster.jpg',
    alt: 'Free AI Video Editor character replacement with preserved motion',
    duration: 'PT5.046S',
    durationLabel: '5-second example',
    uploadDate: '2026-07-26T00:00:00+00:00',
    publishedLabel: 'July 26, 2026',
  },
  {
    title: 'Restyle motion without losing the subject design',
    description:
      'The anime restyle example shows the visual consistency to inspect when a prompt changes style. A useful AI video editing prompt names the desired treatment and states which subject, framing, motion, and scene details must remain stable.',
    prompt:
      'Apply a clean anime treatment while preserving the subject design, motion timing, camera path, composition, and important scene details.',
    video: '/video-examples/example-anime-motion.webm',
    poster: '/video-examples/example-anime-motion-poster.jpg',
    alt: 'AI video editor anime restyle target with stable character motion',
    duration: 'PT5.046S',
    durationLabel: '5-second example',
    uploadDate: '2026-07-26T00:00:00+00:00',
    publishedLabel: 'July 26, 2026',
  },
  {
    title: 'Repair a portrait while keeping natural expression',
    description:
      'The portrait repair example provides a quality target for portrait repair and relighting. Review facial identity, blinking, mouth shape, skin texture, hair edges, and background stability instead of judging the edit from a single poster frame.',
    prompt:
      'Repair unstable facial details and relight the portrait naturally while preserving identity, expression timing, head movement, and camera framing.',
    video: '/video-examples/feature-expression-sync.webm',
    poster: '/video-examples/feature-expression-sync-poster.jpg',
    alt: 'Portrait expression target for an AI video repair and relighting edit',
    duration: 'PT5.046S',
    durationLabel: '5-second example',
    uploadDate: '2026-07-26T00:00:00+00:00',
    publishedLabel: 'July 26, 2026',
  },
] as const;

export const GROK_VIDEO_EDITOR_FEATURES = [
  {
    title: 'Edit an upload or a saved AI video',
    body: 'Start with an MP4 or MOV from your device, or choose a completed result from Video AI History. The Free AI Video Editor accepts clips from 1 to 30 seconds and keeps the chosen video, instruction, selected model, reference image, and settings in a versioned draft across login.',
    outcome:
      'Use upload mode for external footage and History mode when the source already belongs to a Grok image-to-video or text-to-video workflow.',
  },
  {
    title: 'Edit videos with AI prompts',
    body: 'Use the AI video editor with prompt controls to describe what should change, then list what must remain stable: identity, action, timing, framing, camera movement, lighting, and audio. This prompt video editor works best when each pass asks for one primary change, such as replacing a background.',
    outcome:
      'A focused edit video with text prompt request is easier to judge and cheaper to refine than several unrelated changes in one pass.',
  },
  {
    title: 'Guide appearance with a reference image',
    body: 'When the selected model supports visual guidance, add one JPG, PNG, or WebP reference for a person, product, wardrobe item, color treatment, or style. Reference guidance does not guarantee exact identity, text, logo, or geometry preservation.',
    outcome:
      'Use the clearest front-facing or product-focused reference available, then inspect every moving frame for drift.',
  },
  {
    title: 'Match model cost to the current decision',
    body: 'Use the lowest-credit compatible model and a draft resolution while the prompt, motion, or composition is still changing. Move to a model with stronger reference, audio, duration, or resolution controls only after a cheaper result proves the direction.',
    outcome:
      'The low-cost-first model workflow protects credits and makes each upgrade solve a specific quality problem instead of repeating an uncertain edit at a higher price.',
  },
  {
    title: 'Compare and download without watermark',
    body: 'The Free AI Video Editor keeps the source reviewable while the edit runs. When the output finishes, switch between Original and Edited, play the complete clip, and download without an added visible site watermark after checking temporal consistency.',
    outcome:
      'Review faces, hands, small text, reflections, object edges, background geometry, motion cadence, and audio continuity.',
  },
] as const;

export const GROK_VIDEO_EDITOR_MODEL_GUIDE = [
  {
    stage: 'Test the direction',
    choose: 'Lowest-credit compatible model at 480p or 720p',
    useWhen:
      'The prompt, background, object, style, or motion direction is still uncertain',
    checkpoint:
      'Approve the main change and preservation rules before spending more',
  },
  {
    stage: 'Refine a proven edit',
    choose: 'Balanced model with the required reference or audio controls',
    useWhen:
      'The direction works, but identity, product appearance, audio, or duration needs better control',
    checkpoint: 'Compare the complete clip for drift before raising resolution',
  },
  {
    stage: 'Create a high-fidelity candidate',
    choose: 'Premium model at 1080p',
    useWhen:
      'A lower-cost draft already has the right scene, timing, camera path, and subject behavior',
    checkpoint:
      'Use one candidate for final review instead of exploring prompts at premium cost',
  },
  {
    stage: 'Finish for delivery',
    choose: '4K-capable model or upscale an approved result',
    useWhen:
      'The edit has passed motion, identity, text, edge, reflection, and audio checks',
    checkpoint:
      'Do not use 4K to discover the creative direction; it magnifies cost, not prompt quality',
  },
] as const;

export const GROK_VIDEO_EDITOR_LIMITS = [
  {
    title: 'Not a frame-accurate timeline editor',
    body: 'The editor applies one prompt to a complete short clip. Use a timeline editor after the AI edit when delivery requires exact cuts, caption timing, transitions, or multi-scene sequencing.',
  },
  {
    title: 'Model compatibility sets the source range',
    body: 'The selected model determines the accepted duration, output resolution, aspect-ratio behavior, reference-image support, audio controls, and credit cost. The workbench shows the current options before generation.',
  },
  {
    title: 'Higher resolution does not repair a weak direction',
    body: 'Test the prompt and visible change at a lower-cost resolution first. Move to 1080p or 4K only after motion, composition, identity, and preservation rules already work.',
  },
  {
    title: 'Reference guidance is not exact replication',
    body: 'A reference image can guide a person, product, wardrobe item, or visual style, but it does not guarantee exact identity, text, logo, or geometry preservation.',
  },
] as const;

export const GROK_VIDEO_EDITOR_STEPS = [
  {
    name: 'Choose a short source video',
    text: 'Upload an MP4 or MOV up to 50MB and 1–30 seconds, or sign in and choose a completed video from History. The Free AI Video Editor reads the duration and frame dimensions before showing compatible models and a credit estimate.',
  },
  {
    name: 'Describe one visible change',
    text: 'Write an AI video editing prompt of at least five characters that names the requested change and the details to preserve. Add a reference image only when appearance, identity, object shape, or style needs an extra visual anchor.',
  },
  {
    name: 'Choose the least expensive model that fits',
    text: 'The Free AI Video Editor shows only models that accept the source duration, along with their current credit estimate and available controls. Start at 480p or 720p while testing the prompt, then pay for stronger reference, audio, duration, or resolution capabilities only when the edit needs them.',
  },
  {
    name: 'Run the edit and inspect the full clip',
    text: 'Check the displayed credit cost, start the asynchronous edit, and keep working while it processes. Compare Original and Edited from beginning to end, then download or return to the prompt for a narrower revision.',
  },
] as const;

export const GROK_VIDEO_EDITOR_CONTROLS = [
  {
    control: 'Edit model',
    options:
      'Live list of compatible models and their current credit estimates',
    boundary:
      'Start with the lowest-cost option that supports the required input and controls',
  },
  {
    control: 'Input video',
    options: 'MP4 or MOV upload; completed Video AI History result',
    boundary: '1–30 seconds and up to 50MB; each model has a narrower range',
  },
  {
    control: 'Edit instruction',
    options: 'Change background, replace object, relight, repair, or restyle',
    boundary: 'One primary change plus explicit preservation rules',
  },
  {
    control: 'Reference image',
    options: 'Optional JPG, PNG, or WebP when the selected model supports it',
    boundary:
      'One guide image; exact identity and logo fidelity are not guaranteed',
  },
  {
    control: 'Resolution',
    options: '480p to 4K, depending on the selected model',
    boundary: 'Available resolutions and credit cost update with the model',
  },
  {
    control: 'Aspect ratio',
    options: 'Source ratio or a model-supported ratio',
    boundary:
      'A ratio change may require the model to recompose or crop details',
  },
  {
    control: 'Audio',
    options: 'Model default, preserve original audio, or generate audio',
    boundary: 'Always listen to the full result before publishing',
  },
] as const;

export const GROK_VIDEO_EDITOR_USE_CASES = [
  {
    title: 'Reels and TikTok: create social media variations',
    body: 'Use the free AI video editor for Reels, TikTok, and social media clips when one vertical performance needs a new background, wardrobe color, or visual style. Keep the subject, timing, and camera movement stable, then review the complete 9:16 result.',
  },
  {
    title: 'YouTube Shorts: revise a vertical clip',
    body: 'The free AI video editor for YouTube Shorts is designed for short source clips, not long-form timeline editing. Restyle a scene or replace a visible object, then check text, faces, motion, and audio before publishing the Short.',
  },
  {
    title: 'Product ads: revise a shot with a reference',
    body: 'Upload a short product video and a clean packshot, then ask the editor to replace the visible item while preserving hand motion, reflections, camera movement, and scene lighting. Treat the output as a concept variant and inspect every frame before delivery.',
  },
] as const;

export const GROK_VIDEO_EDITOR_TRUST_SIGNALS = [
  {
    value: 'Before every run',
    label: 'Visible credit estimate',
    body: 'The Free AI Video Editor recalculates the displayed cost when the model, duration, resolution, or supported audio setting changes, so users can make the spending decision before generation.',
  },
  {
    value: '1–30s',
    label: 'Validated source range',
    body: 'The Free AI Video Editor reads source duration and dimensions, accepts MP4 or MOV files up to 50MB, and validates the source again on the server.',
  },
  {
    value: 'Original and Edited',
    label: 'Result comparison',
    body: 'The Free AI Video Editor keeps the source reviewable while the edit runs, then lets users play the original and completed result before downloading.',
  },
] as const;

export const GROK_VIDEO_EDITOR_FAQS = [
  {
    question: 'What does free mean in the Free AI Video Editor?',
    answer:
      'You can open and configure the Free AI Video Editor without payment. Starting an edit requires sign-in and uses the credit estimate shown before generation. New accounts currently receive 30 starter credits; additional edits require an available credit balance.',
  },
  {
    question: 'Can I download an AI video edit without watermark?',
    answer:
      'The Free AI Video Editor does not add a visible site watermark to completed downloads. AI providers may still attach invisible provenance or metadata, so inspect the downloaded file before publishing.',
  },
  {
    question: 'How do I use the Free AI Video Editor with a prompt?',
    answer:
      'To use the Free AI Video Editor with a prompt, upload a short source video, choose one visible change, and state what must remain stable. For example, replace a studio background while preserving the performer, action timing, camera path, lighting direction, and original audio. Add a reference image when a person, product, or style needs a visual anchor.',
  },
  {
    question: 'How should I choose an AI video model without wasting credits?',
    answer:
      'Start with the lowest-credit compatible model at 480p or 720p while the prompt is still changing. Upgrade only when a cheaper result proves the scene and a specific capability, such as reference guidance, audio, longer duration, 1080p, or 4K, is still needed. The Free AI Video Editor shows the current model cost before every run.',
  },
  {
    question: 'Is this an AI video editor or an AI video generator?',
    answer:
      'The Free AI Video Editor changes an existing upload or saved video. The AI Video Generator creates a new clip from text. Generate a new scene first when needed, then return to the editor to revise the saved result.',
  },
] as const;

export const GROK_VIDEO_EDITOR_SCHEMA_FEATURES = [
  'Free online AI video editing for MP4 and MOV clips',
  'Prompt-based background, object, repair, and style changes',
  'Completed Video AI History input',
  'Expandable model selection with a visible pre-generation credit estimate',
  'Model-specific reference image guidance',
  '480p through 4K model-specific output controls',
  'Original or generated audio controls where supported',
  'Original and edited video comparison',
  'Download without an added visible site watermark',
];

export function getGrokVideoEditorSeoData() {
  return {
    path: GROK_VIDEO_EDITOR_PATH,
    datePublished: GROK_VIDEO_EDITOR_DATE_PUBLISHED,
    lastUpdated: GROK_VIDEO_EDITOR_LAST_UPDATED,
    lastUpdatedLabel: GROK_VIDEO_EDITOR_LAST_UPDATED_LABEL,
    ogImage: GROK_VIDEO_EDITOR_OG_IMAGE,
    seoTitle: GROK_VIDEO_EDITOR_SEO_TITLE,
    seoDescription: GROK_VIDEO_EDITOR_SEO_DESCRIPTION,
    keywords: GROK_VIDEO_EDITOR_KEYWORDS,
    directAnswer: GROK_VIDEO_EDITOR_DIRECT_ANSWER,
    quickAnswer: GROK_VIDEO_EDITOR_QUICK_ANSWER,
    relatedTools: GROK_VIDEO_EDITOR_RELATED_TOOLS,
    proofExamples: GROK_VIDEO_EDITOR_PROOF_EXAMPLES,
    features: GROK_VIDEO_EDITOR_FEATURES,
    steps: GROK_VIDEO_EDITOR_STEPS,
    modelGuide: GROK_VIDEO_EDITOR_MODEL_GUIDE,
    limits: GROK_VIDEO_EDITOR_LIMITS,
    controls: GROK_VIDEO_EDITOR_CONTROLS,
    useCases: GROK_VIDEO_EDITOR_USE_CASES,
    trustSignals: GROK_VIDEO_EDITOR_TRUST_SIGNALS,
    faqs: GROK_VIDEO_EDITOR_FAQS,
    schemaFeatures: GROK_VIDEO_EDITOR_SCHEMA_FEATURES,
  };
}
